import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { CSpinner } from '@coreui/react'
import api from '../utils/apiClient'
import TemporaryErrorScreen from './TemporaryErrorScreen'

// ─────────────────────────────────────────────────────────────────────────────
// États du statut de profil — incident 2026-08-19.
//
// Auparavant `isVerified` valait null | true | false, et TOUTE erreur non-401
// sur /members/profile-status écrivait `false`. Conséquence : quand PM2 tuait
// l'API (502 nginx pendant le redémarrage), des membres parfaitement onboardés
// étaient redirigés vers /verify-profile, écran vide + « Erreur lors du
// chargement de votre profil ». Un refresh « corrigeait » le problème, ce qui
// rendait le bug fuyant et incompréhensible côté utilisateur.
//
// RÈGLE : on n'atteint /verify-profile que sur une information métier
// AFFIRMATIVE du backend (`isProfileVerified === false`), jamais par défaut à
// cause d'une absence de réponse. « Je ne sais pas » est un état distinct de
// « le profil n'est pas vérifié ».
// ─────────────────────────────────────────────────────────────────────────────
const STATUS = {
  LOADING: 'loading',
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified',
  ERROR: 'error',
}

// Backoff court et BORNÉ : on absorbe un redémarrage d'API (quelques secondes)
// sans jamais boucler indéfiniment — au-delà, l'écran d'erreur avec bouton
// « Réessayer » rend la main à l'utilisateur.
const RETRY_DELAYS_MS = [400, 1500]

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Une erreur transitoire est une erreur dont on peut espérer qu'un retry
 * immédiat réussisse : coupure réseau, timeout, API en cours de redémarrage.
 * Une 4xx applicative (403, 404…), elle, se reproduira à l'identique.
 */
const isTransientError = (error) => {
  // L'intercepteur d'apiClient normalise en `error.status`, mais une erreur
  // réseau/timeout est rejetée telle quelle par axios : elle n'a NI `status`
  // NI `response`. C'est précisément le cas « le serveur n'a pas répondu ».
  const status = error?.status ?? error?.response?.status
  if (status === undefined) return true

  return status === 408 || status === 429 || status >= 500
}

const ProtectedRoute = ({ children }) => {
  const [profileStatus, setProfileStatus] = useState(STATUS.LOADING)
  const [loading, setLoading] = useState(true)
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false)
  const location = useLocation()
  // ✅ SECURITY: Vérification basée sur userId (pas de token JS)
  // La vraie validation se fait côté serveur via le cookie httpOnly
  const userId = localStorage.getItem('userId')

  // ✅ NOUVEAU : États pour l'onboarding admin
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminOnboardingDone, setAdminOnboardingDone] = useState(null)

  // Vérifier si l'utilisateur doit changer son mot de passe temporaire
  const hasTempPassword = localStorage.getItem('tempPassword') === 'true'

  // Les retries introduisent des `await` entre le démontage du composant et la
  // reprise de la boucle : sans cette garde, on écrirait dans le state d'un
  // composant démonté (navigation pendant une tentative).
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const checkProfileStatus = useCallback(async () => {
    if (hasCheckedStatus) return

    for (let attempt = 0; ; attempt += 1) {
      try {
        const response = await api.get('/members/profile-status')
        if (!isMountedRef.current) return

        setProfileStatus(response.isProfileVerified ? STATUS.VERIFIED : STATUS.UNVERIFIED)

        // ✅ NOUVEAU : Récupérer le rôle depuis la réponse ou le token
        const userRole = response.role || localStorage.getItem('userRole')
        const userIsAdmin = userRole === 'admin'
        setIsAdmin(userIsAdmin)

        // ✅ NOUVEAU : Si admin, vérifier l'onboarding admin
        if (userIsAdmin) {
          try {
            const onboardingResponse = await api.get('/organization/admin-onboarding-status')
            if (!isMountedRef.current) return
            setAdminOnboardingDone(onboardingResponse.onboardingComplete)
          } catch (onboardingError) {
            console.warn('Erreur vérification onboarding admin:', onboardingError)
            // En cas d'erreur, on considère l'onboarding comme fait pour ne pas bloquer
            setAdminOnboardingDone(true)
          }
        } else {
          // Non-admin : pas d'onboarding admin
          setAdminOnboardingDone(true)
        }

        setHasCheckedStatus(true)
        setLoading(false)
        return
      } catch (error) {
        if (!isMountedRef.current) return

        if (error.response?.status === 401 || error.status === 401) {
          // ✅ SECURITY: Nettoyer localStorage (préserver données admin)
          const adminUser = localStorage.getItem('adminUser')
          const adminUserId = localStorage.getItem('adminUserId')
          const lang = localStorage.getItem('terrilink_lang')
          localStorage.clear()
          if (adminUser) localStorage.setItem('adminUser', adminUser)
          if (adminUserId) localStorage.setItem('adminUserId', adminUserId)
          if (lang) localStorage.setItem('terrilink_lang', lang)
          setLoading(false)
          window.location.replace('/login')
          return
        }

        if (isTransientError(error) && attempt < RETRY_DELAYS_MS.length) {
          console.warn(
            `[ProtectedRoute] profile-status indisponible (tentative ${attempt + 1}/${
              RETRY_DELAYS_MS.length + 1
            })`,
            error?.message,
          )
          await sleep(RETRY_DELAYS_MS[attempt])
          if (!isMountedRef.current) return
          continue
        }

        // Épuisé ou non-retryable : on ASSUME l'ignorance plutôt que de
        // conclure « profil non vérifié ». Cf. le bandeau STATUS en tête.
        console.error('Erreur vérification profil:', error.response || error)
        setProfileStatus(STATUS.ERROR)
        setHasCheckedStatus(true)
        setLoading(false)
        return
      }
    }
  }, [hasCheckedStatus])

  // Relance manuelle depuis l'écran d'erreur : on repasse en LOADING et on
  // rouvre la garde `hasCheckedStatus`, ce qui redéclenche l'effet ci-dessous.
  const retryProfileStatus = useCallback(() => {
    setProfileStatus(STATUS.LOADING)
    setLoading(true)
    setHasCheckedStatus(false)
  }, [])

  // [AJOUT] Quand on reçoit "auth_updated", on relance la vérification
  // Dans la fonction useEffect qui gère l'écouteur 'auth_updated'
  useEffect(() => {
    function handleAuthUpdate() {
      console.log('🛡️ ProtectedRoute: auth_updated reçu')

      // ✅ SECURITY: Plus de token - vérifier uniquement userId
      const storedUserId = localStorage.getItem('userId')
      console.log('🛡️ Valeurs localStorage:', { userId: storedUserId })

      setProfileStatus(STATUS.LOADING)
      setHasCheckedStatus(false)
      setLoading(true)

      console.log('🛡️ Lancement de checkProfileStatus')
    }

    document.addEventListener('auth_updated', handleAuthUpdate)

    return () => {
      document.removeEventListener('auth_updated', handleAuthUpdate)
    }
  }, [])

  useEffect(() => {
    // ✅ SECURITY: Vérifier via userId (pas de token JS)
    if (userId && !hasCheckedStatus) {
      checkProfileStatus()
    } else {
      setLoading(false)
    }
  }, [userId, checkProfileStatus, hasCheckedStatus])

  // Afficher le spinner pendant le chargement
  if (loading) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center">
        <CSpinner />
      </div>
    )
  }

  // ✅ SECURITY: Redirection vers login si pas de userId
  // (la vraie validation se fait côté serveur via cookie httpOnly)
  if (!userId) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Redirection vers change-password si nécessaire
  if (hasTempPassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  // ✅ NOUVEAU : Redirection vers onboarding admin si nécessaire (AVANT verify-profile)
  // Uniquement pour les admins avec onboarding non terminé
  if (
    !hasTempPassword &&
    isAdmin &&
    adminOnboardingDone === false &&
    location.pathname !== '/admin-onboarding'
  ) {
    return <Navigate to="/admin-onboarding" replace />
  }

  // Redirection depuis admin-onboarding si déjà terminé
  if (adminOnboardingDone === true && location.pathname === '/admin-onboarding') {
    return <Navigate to="/verify-profile" replace />
  }

  // Statut indéterminé (API injoignable) : on ne redirige NULLE PART. Rediriger
  // ici reviendrait à inventer une information métier qu'on n'a pas.
  if (profileStatus === STATUS.ERROR) {
    return <TemporaryErrorScreen onRetry={retryProfileStatus} />
  }

  // Redirection vers verify-profile si nécessaire
  if (
    !hasTempPassword &&
    profileStatus === STATUS.UNVERIFIED &&
    location.pathname !== '/verify-profile' &&
    location.pathname !== '/admin-onboarding'
  ) {
    return <Navigate to="/verify-profile" replace />
  }

  // Redirection vers dashboard si déjà vérifié
  if (profileStatus === STATUS.VERIFIED && location.pathname === '/verify-profile') {
    return <Navigate to="/publications" replace />
  }

  return React.isValidElement(children)
    ? React.cloneElement(children, { onVerificationSuccess: checkProfileStatus })
    : children
}

export default ProtectedRoute
